import { AccordionDetails, Box, Container, Grid, Typography, useTheme } from "@mui/material";
import { Accordion, RenderHTML, SortDropdown } from "components";
import { MouseEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ELEMENT_ID, MyLearning_Link_Paths, SORT_ALPHABETICAL_ASCENDING } from "utils/constants";
import { useTranslation } from "react-i18next";
import { SortType } from "utils/types";
import { CardWithTitle } from "components/ibf-components/card-with-title/card-with-title";
import { useStyles } from "./landingContainerV2-Styles";
import { ErrorToastPayload, sortData } from "utils/helpers";
import { useSelector } from "react-redux";
import { LOAD_SUBJECTS, RootState, useAppDispatch } from "store";
import { useLazyQuery, useQuery } from "@apollo/client/react";
import { GET_PRODUCTS } from "utils/queries";
import { Products, Products_products } from "utils/graphql/Products";
import { ProductType } from "utils/graphql/Global";
import LoadMoreButton from "components/ibf-components/loadmore/loadmore";
import useMediaQuery from "@mui/material/useMediaQuery";
import portalSettingsManager from "utils/portalSettingsManager";
import { requestOpenSurvey } from "store/newLearningRequest";
import { loadSubjects } from "store/core";

export const LandingContainerV2 = (): React.ReactElement => {
  const { t } = useTranslation();
  const classes = useStyles();
  const navigate = useNavigate();
  const subjectsStore = useSelector((state: RootState) => state.core.subjects);
  const [sort, setSort] = useState<SortType>(SORT_ALPHABETICAL_ASCENDING);
  const [subjects, setSubjects] = useState<Products_products>();
  const dispatch = useAppDispatch();
  const [isLoadable, setIsLoadable] = useState<boolean>(true);
  const [enableLoadmore, setEnableLoadmore] = useState<boolean>(true);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageText, setPageText] = useState("");
  const pageSize = Number(process.env.REACT_APP_IBF_PAGE_SIZE);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    data: productsData,
    loading: productsLoading,
    error: productsError,
  } = useQuery<Products>(GET_PRODUCTS, {
    variables: { productType: ProductType.CHANNEL, pageSize: pageSize, pageNo: pageNumber },
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (productsData) {
      if (subjects && pageNumber > 1) {
        // Only append when loading more pages, not on initial load
        setSubjects((prevState) => ({
          ...prevState,
          children: sortData(sort, [
            ...(prevState?.children || []),
            ...(productsData?.products?.children || []),
          ]),
        }));
      } else {
        dispatch({ type: LOAD_SUBJECTS, payload: productsData });
        setSubjects(productsData?.products);
      }
    }
  }, [productsData]);

  useEffect(() => {
    if (productsError) {
      dispatch({
        type: "alert/show",
        payload: ErrorToastPayload(t("home"), t("ibf_error_message")),
      });
    }
  }, [productsError, dispatch, t]);

  const loadMore = () => {
    setPageNumber((prevPage) => {
      const newPage = prevPage + 1;
      loadSubjects();
      return newPage;
    });
  };
  useEffect(() => {
    if (!subjectsStore?.products) {
      loadSubjects();
    } else {
      setSubjects(subjectsStore?.products);
    }
  }, []);

  useEffect(() => {
    setIsLoadable(subjects?.children?.length < subjects?.noOfChildren);
    setPageText(
      `${t("showing")} ${subjects?.children?.length} ${t("of")} ${subjects?.noOfChildren}`,
    );
    setEnableLoadmore(
      subjectsStore?.products?.children?.length !== subjectsStore?.products?.noOfChildren &&
        !!subjectsStore?.products?.children,
    );
  }, [subjects]);

  useEffect(() => {
    dispatch({
      type: "loader/showandhide",
      payload: { show: productsLoading, message: t("loading") },
    });
  }, [productsLoading]);

  const handleCardClick = (id: string) => {
    navigate(`/subjectarea/${id}`);
  };

  const sortSubjects = (sortType: SortType) => {
    if (subjects?.children) {
      setSort(sortType);
      const sortedContents = sortData(sortType, subjects.children);
      setSubjects({
        ...subjects,
        children: sortedContents,
      });
    }
  };

  const handleBookingContentClick = (e: MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const anchorEl = target.closest('[data-anchor="newLearningRequirement"]');
    if (!anchorEl) return;

    e.preventDefault();
    if (portalSettingsManager.application.features.enableNewLearningRequest) {
      dispatch(requestOpenSurvey());
    } else {
      navigate(MyLearning_Link_Paths.LearningRequest);
    }
  };

  return (
    <Container className={classes.container}>
      {portalSettingsManager?.application.features.enableBanner && (
        <Box className={classes.accordionWrapper}>
          <Accordion
            title={
              <Typography
                className={classes.accordionTitle}
                component="div"
                id="po_booking_instructions"
              >
                <RenderHTML content={t("po_booking_instructions")} />
              </Typography>
            }
            positionArrow="right"
            mobile={isMobile}
            expanded={false}
            buttonText="Click to expand/collapse"
          >
            <AccordionDetails
              id="po_booking_content"
              role="region"
              aria-labelledby="po_booking_instructions"
              onClick={handleBookingContentClick}
            >
              <RenderHTML
                content={t("po_booking_content", {
                  contactEmail: portalSettingsManager?.application?.common?.contactUs?.emailId,
                  phoneNo: portalSettingsManager?.application?.features?.phoneNo,
                })}
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      )}
      <Grid container className={classes.subheader}>
        <Grid size={{ md: 9, sm: 8, xs: 4 }} className={classes.subheaderDescription}>
          <Box id={`${ELEMENT_ID.SUBJECTAREA}-${ELEMENT_ID.PAGETEXT}`}>
            {!productsLoading && subjects?.children && (
              <Typography variant="h1" className={classes.pageText} aria-labelledby={pageText}>
                {pageText}
              </Typography>
            )}
          </Box>
        </Grid>
        <Grid size={{ md: 3, sm: 4, xs: 8 }} id={`${ELEMENT_ID.SUBJECTAREA}-${ELEMENT_ID.SORT}`}>
          <SortDropdown sort={sort} selectSort={sortSubjects} />
        </Grid>
      </Grid>
      <Box>
        <Grid container spacing={3} component="ul" className={classes.subjectsList}>
          {subjects?.children?.map((item: any, idx: number) => {
            return (
              <Grid
                size={{ md: 3, sm: 6, xs: 12 }}
                key={idx}
                component="li"
                className={classes.subjectItem}
              >
                <CardWithTitle
                  id={`${ELEMENT_ID.SUBJECTAREA}-${idx}`}
                  item={item}
                  key={idx}
                  onCardClick={() => handleCardClick(item.id)}
                />
              </Grid>
            );
          })}
        </Grid>
      </Box>
      <Box>
        <LoadMoreButton
          loadMore={loadMore}
          isLoadable={isLoadable}
          loading={productsLoading}
          enableLoadmore={enableLoadmore}
        />
      </Box>
    </Container>
  );
};
